import { isAxiosError } from "axios";
import { z } from "zod";

import { SESSION_UNAVAILABLE_CODE } from "@/lib/axios";

/**
 * Some backend endpoints return `{"error": str(e)}` for a Django ValidationError,
 * whose `str()` is a Python list repr like `"['Message here']"`. Unwrap that to
 * the plain message(s). Anything that isn't a bracketed list is returned as-is,
 * so ordinary messages pass through untouched.
 */
function unwrapPyListRepr(message: string): string {
  const trimmed = message.trim();
  if (!/^\[.*\]$/.test(trimmed)) return message;
  const segments = trimmed.match(/(['"])(.*?)\1(?=\s*,\s*|\s*\]?$)/g);
  if (!segments || segments.length === 0) return message;
  return segments.map((s) => s.slice(1, -1)).join(" | ");
}

// Bare-list body: ["general error", ...] — non-string members stringified.
const generalErrorListSchema = z.array(z.coerce.string());

// The well-known single-message DRF fields; each tolerates a non-string
// value by degrading to undefined so the next candidate is tried.
const knownMessageFieldsSchema = z.object({
  detail: z.string().optional().catch(undefined),
  message: z.string().optional().catch(undefined),
  error: z.string().optional().catch(undefined),
});

// Any plain object body (rejects arrays/strings); keys stay unvalidated for
// the field-flattening fallback.
const objectBodySchema = z.looseObject({});

// Service-raised ValidationError({"field": "msg"}) reaches the client as
// {"error": {"field": ["msg"]}} (every inventory view's `except
// DjangoValidationError` wraps exc.message_dict this way) -- unwrap that
// inner dict before flattening, or the object stringifies as a useless
// "[object Object]".
const wrappedFieldDictSchema = z.object({ error: z.looseObject({}) });

// DRF validation-error body: {field: [messages], ...} — join each field's
// messages into one line.
function flattenFieldMessages(body: z.infer<typeof objectBodySchema>): string {
  return Object.values(body)
    .map((messages) =>
      Array.isArray(messages) ? messages.join(", ") : `${messages}`,
    )
    .join(" | ");
}

export function formatError(cause: unknown, defaultMsg?: string) {
  if (cause instanceof Error && !isAxiosError(cause)) return cause.message;
  if (!isAxiosError(cause)) return defaultMsg ?? "Something went wrong";
  // The refresh got no verdict (T-245): its message says what happened,
  // which beats any caller default.
  if (cause.code === SESSION_UNAVAILABLE_CODE) return cause.message;
  let errorMessage = defaultMsg || "Something went wrong";

  if (cause.response) {
    const body: unknown = cause.response.data;

    const generalErrors = generalErrorListSchema.safeParse(body);
    const objectBody = objectBodySchema.safeParse(body);

    if (generalErrors.success) {
      errorMessage = generalErrors.data.join(" | ");
    } else if (objectBody.success) {
      const known = knownMessageFieldsSchema.parse(objectBody.data);
      if (known.detail) {
        errorMessage = known.detail;
      } else if (known.message) {
        errorMessage = known.message;
      } else if (known.error) {
        errorMessage = known.error;
      } else {
        const wrapped = wrappedFieldDictSchema.safeParse(objectBody.data);
        const flattened = flattenFieldMessages(
          wrapped.success ? wrapped.data.error : objectBody.data,
        );
        if (flattened) errorMessage = flattened;
      }
    } else {
      errorMessage = cause.message;
    }
  }

  return unwrapPyListRepr(errorMessage);
}
